import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Accordion, Button, Card, Heading,
} from '@deloitte/gel-library'
import some from 'lodash/some'
import constants from 'constants'
import { formDestroy, formInit, formUpdate } from 'actions/forms'
import { sortByFunction } from 'utils/arrays'
import { handleError, requiredFields, validateField } from 'utils/validators'
import { schemaWithTests } from 'utils/business/parameters'
import Messagebar from 'components/Messagebar'
import {
  getCurrentDisplayValues,
  isConditionallyDisabledFromParameterDisplay,
  isConditionallyDisabledFromParameterValues,
} from 'actionHub/utils/dependencies'
import { SavePackageParameters, SaveParameters, updateJob } from 'actions/job'
import { getAllParameters } from 'utils/business/jobs'
import ErrorOutlinedIcon from '@material-ui/icons/ErrorOutlined'
import { isEqual } from 'lodash'
import { notify } from 'actions/app'
import { isNullOrWhiteSpace } from 'utils/strings'
import Parameter from './components/Parameter'

import styles from './InputData.scss'

const FORM_ID = constants.FORM_SETUP

const areParametersEqual = (prevProps, nextProps) => {
  return (
    prevProps.field.id === nextProps.field.id
    && prevProps.formData.value === nextProps.formData.value
    && prevProps.formData.error === nextProps.formData.error
    && prevProps.formData.valid === nextProps.formData.valid
    && prevProps.disabled === nextProps.disabled
    && prevProps.field.display === nextProps.field.display
  )
}

const MemorisedParameter = React.memo(Parameter, areParametersEqual)

class EditParameters extends Component {
  EDITABLE_FIELD_TYPES = [
    'TABLE',
    'DROPDOWN',
    'COMBO BOX',
    'BIT',
    'BOOLEAN',
    'DATE',
    'TIME',
    'DATETIME',
    'INT',
    'CHAR',
    'VARCHAR',
    'NVARCHAR',
    'FLOAT',
    'MONEY',
  ]

  static propTypes = {
    app: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    dispatch: PropTypes.func.isRequired,
    forms: PropTypes.object.isRequired,
    job: PropTypes.shape({
      _isSettingCategoryParameters: PropTypes.bool,
      _isSettingSolutionParameters: PropTypes.bool,
      analyses: PropTypes.arrayOf(PropTypes.object),
      packageId: PropTypes.string,
      packageParameters: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
  }

  static defaultProps = {
    disabled: false,
  }

  state = {
    expandedKey: '',
    originalAnalysesParameterFields: [],
    originalSolutionParameterFields: [],
    isDirty: false,
    isSolutionParamDirty: [],
    localFormValues: {},
  }

  componentDidMount() {
    const { dispatch, job } = this.props
    this.updateJobParametersDisplayStatus(getAllParameters(job))
    dispatch(formInit(FORM_ID, schemaWithTests(job)))
    this.mapAnalysesOriginalFieldAndValue()
  }

  componentWillUnmount() {
    const { dispatch } = this.props
    dispatch(formDestroy(FORM_ID))
  }

  updateDisplayStatus = (currentParameters) => {
    this.updateJobParametersDisplayStatus(currentParameters)
  }

  handleValidation = (type, field, value, context) => {
    const {
      dispatch, forms, job,
    } = this.props

    if (!forms[FORM_ID] || !forms[FORM_ID][field]) {
      return
    }

    const errorObj = validateField(
      requiredFields(schemaWithTests(job).formFields),
      field,
      value,
      forms[FORM_ID],
      context,
    )

    if (errorObj) {
      dispatch(formUpdate(FORM_ID, handleError(errorObj, field)))
    }
  }

  handleFormUpdate = (type, field, value) => {
    const { dispatch, forms } = this.props
    const isSimpleField = type === 'BIT' || type === 'boolean'
    if (!isSimpleField) {
      const formEntries = Object.entries(forms[FORM_ID])
      const currentParameters = formEntries.filter(([formKey]) => !formKey.startsWith('_'))
      const updatedParameters = currentParameters.map(([, formValue]) => (
        `${formValue.meta.parameter.id}` === field
          ? { ...formValue, value }
          : formValue
      ))

      this.updateDisplayStatus(updatedParameters)
    }

    const currentFieldState = forms[FORM_ID] && forms[FORM_ID][field]
    const stateValue = {
      data: {
        value,
        valid: currentFieldState ? currentFieldState.valid : true,
        error: currentFieldState ? currentFieldState.error : '',
      },
      field,
    }
    dispatch(formUpdate(FORM_ID, stateValue))

    this.checkIsDirtyCategoryAnalysis(field, value)
    this.checkIsDirtySolutionParams(field, value)
  }

  handleAccordianClick = (key) => {
    this.setState(prevState => ({
      expandedKey: prevState.expandedKey === key ? '' : key,
    }))
  }

  mapAnalysesOriginalFieldAndValue = () => {
    const { job } = this.props
    const originalAnalysesParameterFields = []
    job.analyses.map(a => a.parameters?.map(ap => originalAnalysesParameterFields.push({
      type: ap.type, id: ap.id, category: ap.category, originalValue: ap.value, analysisId: a.id,
    })))
    this.setState({ originalAnalysesParameterFields })
    this.setState({ isDirty: false })

    const originalSolutionParameterFields = []
    job.packageParameters.map(ap => originalSolutionParameterFields.push({
      type: ap.type, id: ap.id, originalValue: ap.value,
    }))
    this.setState({ originalSolutionParameterFields })
    this.setState({ isSolutionParamDirty: [] })
  }

  getDirtyParameters = (analysis, key) => {
    const { originalAnalysesParameterFields } = this.state
    const { forms } = this.props
    const formEntries = Object.entries(forms[FORM_ID])
    const originalFieldsToCheck = originalAnalysesParameterFields.filter(oapr => oapr.analysisId === analysis.id && oapr.category === key)
    const formFieldsToCheck = formEntries.filter(([formKey]) => !formKey.startsWith('_') && originalFieldsToCheck.map(o => o.id).includes(formKey))
    const updatedFormFielsToCheck = formFieldsToCheck.map(([k, v]) => {
      return { id: k, newValue: v.value }
    })

    return originalFieldsToCheck
      .filter(o => !isEqual(updatedFormFielsToCheck.find(u => u.id === o.id).newValue, o.originalValue))
      .map(fo => ({ ...fo, newValue: updatedFormFielsToCheck.find(uff => uff.id === fo.id).newValue }))
  }

  renderMemorisedParameter = (field, index, disabled = false) => {
    const { forms, dispatch, disabled: propsDisabled } = this.props
    const form = forms[FORM_ID]
    const fieldId = field.id
    const formKey = field.formKey || fieldId
    const isDisabled = disabled || propsDisabled

    return (
      <MemorisedParameter
        dataTestId={fieldId}
        disabled={isDisabled}
        field={field}
        formData={this.getCurrentFormValue(fieldId, form[formKey])}
        index={index}
        key={fieldId}
        onChange={
          this.EDITABLE_FIELD_TYPES.includes(field.type?.toUpperCase())
            ? (paramFieldId, value) => this.handleChange(field.type, paramFieldId, value)
            : () => {
              dispatch(notify(`Editing is not available for parameter type: ${field.type}`, 'error'))
            }
        }
        onValidation={(paramFieldId, value, context) => this.handleValidation(field.type, paramFieldId, value, context)}
      />
    )
  }

  renderPillar = (test, dictionary, form, formKeys) => {
    const { expandedKey, isDirty } = this.state
    const {
      disabled, job, dispatch,
    } = this.props
    const { analyses, packageParameters } = job
    const selectedAnalyses = analyses.filter(a => a.selected)

    const currentDisplayValues = getCurrentDisplayValues(selectedAnalyses, packageParameters)

    return (
      <div>
        { Object.keys(dictionary).map((key) => {
          const fields = dictionary[key].map(p => ({
            ...p,
            formKey: `${p.id}`,
          }))
            .filter(x => formKeys.includes(x.formKey))
          const completeFields = fields.filter((f) => {
            const val = form[f.id]?.value
            if (Array.isArray(val)) {
              return val.length > 0
            }
            return !isNullOrWhiteSpace(val)
          })
          const completeStatus = fields.length > 0
            ? `${completeFields.length} of ${fields.length} completed`
            : ''

          const showError = fields.some(field => !form[field.id]?.valid)

          return (
            <Accordion
              className={styles.accordion}
              expanded={expandedKey === key}
              heading={(
                <div className={styles.accHeading}>
                  <div className={styles.pillarName}>
                    {key}
                    {showError && (
                      <span className={styles.errorsFound}>
                        <ErrorOutlinedIcon className={styles.errorIcon} />
                        Errors found
                      </span>
                    )}
                  </div>
                  <div>
                    <span className={styles.completionText}>{completeStatus}</span>
                  </div>
                </div>
              )}
              key={key}
              onChange={() => this.handleAccordianClick(key)}
            >
              <div className={styles.withScroll}>
                {fields.map((field, fieldIndex) => {
                  if (field.display && !isConditionallyDisabledFromParameterDisplay(field, currentDisplayValues)) {
                    return this.renderMemorisedParameter(field, fieldIndex, disabled)
                  }
                  return null
                })}
                <div className={styles.saveButton}>
                  <Button
                    className={styles.save}
                    disabled={!isDirty || job._isSettingCategoryParameters}
                    mode="secondary"
                    onClick={() => dispatch(SaveParameters(() => this.mapAnalysesOriginalFieldAndValue(), this.getDirtyParameters(test, key), key, test))}
                  >
                    {job._isSettingCategoryParameters ? 'SAVING ...' : 'SAVE'}
                  </Button>
                </div>
              </div>
            </Accordion>
          )
        })}
      </div>
    )
  }

  renderTest = ({ test, index }, form, formKeys) => {
    const { disabled, job } = this.props
    const { analyses, packageParameters } = job
    const selectedAnalyses = analyses.filter(a => a.selected)

    const currentDisplayValues = getCurrentDisplayValues(selectedAnalyses, packageParameters)

    const fieldsToShow = test.parameters
      .filter(p => p.category === null)
      .sort(sortByFunction(x => x.displayOrder))
      .map(p => ({
        ...p,
        formKey: `${p.id}`,
      }))
      .filter(x => formKeys.includes(x.formKey))

    const categories = test.parameters.filter(p => p.category !== null).map(p => p.category)
    const uniqueCategories = [...new Set(categories)]
    const dictionary = {}
    uniqueCategories.forEach((uc) => {
      dictionary[uc] = test.parameters.filter(p => p.category === uc).sort(sortByFunction(x => x.displayOrder))
    })

    return (
      <div className={styles.test} key={test.name}>
        <div className="row">
          <div className="col-xs-12 col-sm-offset-2 col-sm-8 col-md-offset-2 col-md-8">
            <div className={index > 0 ? styles.testSection : null}>
              <div>
                <Heading className={styles.testHeading} level={7}>
                  {test.name}
                </Heading>
                {fieldsToShow.map((field, i) => {
                  if (field.display && !isConditionallyDisabledFromParameterDisplay(field, currentDisplayValues)) {
                    return this.renderMemorisedParameter(field, i, disabled)
                  }
                  return null
                })}
                {this.renderPillar(test, dictionary, form, formKeys)}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  checkIsDirtyCategoryAnalysis = (field, value) => {
    const { originalAnalysesParameterFields } = this.state
    const { forms } = this.props
    const formEntries = Object.entries(forms[FORM_ID])
    const originalField = originalAnalysesParameterFields.find(oapr => oapr.id === field)
    const originalFieldsToCheck = originalAnalysesParameterFields.filter(oapr => oapr.analysisId === originalField?.analysisId && oapr.category === originalField?.category)
    const formFieldsToCheck = formEntries.filter(([formKey]) => !formKey.startsWith('_') && originalFieldsToCheck.map(o => o.id).includes(formKey))
    const updatedFormFielsToCheck = formFieldsToCheck.map(([k, v]) => {
      return { id: k, newValue: field === k ? value : v.value }
    })
    this.setState({ isDirty: originalFieldsToCheck.some(o => !isEqual(updatedFormFielsToCheck.find(u => u.id === o.id).newValue, o.originalValue)) })
  }

  checkIsDirtySolutionParams = (field, value) => {
    const { originalSolutionParameterFields, isSolutionParamDirty } = this.state
    const originalField = originalSolutionParameterFields.find(f => f.id === field)
    const isValueChanged = originalField ? !isEqual(value, originalField.originalValue) : false

    if (isValueChanged) {
      const updatedField = {
        id: field,
        originalValue: originalField.originalValue,
        updatedValue: value,
        type: originalField.type,
      }

      const updatedDirtyFields = isSolutionParamDirty.some(f => f.id === field)
        ? isSolutionParamDirty.map(f => (f.id === field ? updatedField : f))
        : [...isSolutionParamDirty, updatedField]

      this.setState({ isSolutionParamDirty: updatedDirtyFields })
    }
  };

  handleChange = (type, field, value) => {
    this.setState(prevState => ({
      localFormValues: {
        ...prevState.localFormValues,
        [field]: value,
      },
    }))

    this.handleFormUpdate(type, field, value)
  }

  getCurrentFormValue = (fieldId, reduxFormData) => {
    const { localFormValues } = this.state
    if (fieldId in localFormValues) {
      return {
        ...reduxFormData,
        value: localFormValues[fieldId],
      }
    }
    return reduxFormData
  }

  isConditionallyDisabledFromDependencies = (parameterInConcern, currentParameters) => {
    const reducedObjForCurrentParameters = {}
    currentParameters.map((cppv) => {
      const { parameter } = cppv.meta
      const { id } = parameter
      const { value } = cppv
      let sqlRepValue = value
      if (value === true) {
        sqlRepValue = '1'
      }
      if (value === false) { sqlRepValue = '0' }

      reducedObjForCurrentParameters[id] = { value: sqlRepValue, display: parameter.display }
      return (
        <div />
      )
    })
    return isConditionallyDisabledFromParameterValues(parameterInConcern, reducedObjForCurrentParameters)
  }

  updateJobParametersDisplayStatus = (currentParameters) => {
    const { dispatch, job } = this.props
    const { packageParameters, analyses } = job
    const selectedAnalyses = analyses.filter(a => a.selected)

    const newPackageParameters = packageParameters.map(pp => ({ ...pp, display: !this.isConditionallyDisabledFromDependencies(pp, currentParameters) }))
    const newAnalyses = selectedAnalyses.flatMap(a => a.parameters)
      .filter(p => p)
      .map(ap => ({ ...ap, display: !this.isConditionallyDisabledFromDependencies(ap, currentParameters) }))

    const updatedPackageParameters = packageParameters.map(pp => ({
      ...pp,
      display: newPackageParameters.find(npp => npp.id === pp.id).display,
    }))
    const updatedAnalyses = selectedAnalyses.map(a => ({
      ...a,
      parameters: a.parameters?.map(ap => ({ ...ap, display: newAnalyses.find(na => na.id === ap.id).display })),
    }))
    dispatch(updateJob({ packageParameters: updatedPackageParameters, analyses: updatedAnalyses }))
  }

  render() {
    const {
      job, forms, dispatch, app,
    } = this.props
    const { isSolutionParamDirty } = this.state
    const { analyses, packageParameters } = job
    const form = forms[FORM_ID]
    if (!form) {
      return <div />
    }
    const formKeys = Object.keys(form)

    const selectedAnalyses = analyses.filter(a => a.selected)

    const currentDisplayValues = getCurrentDisplayValues(selectedAnalyses, packageParameters)

    return (
      <div className={styles.base}>
        {job.packageParameters.length > 0
          && job.packageParameters.some(parameter => parameter.display && !isConditionallyDisabledFromParameterDisplay(parameter, currentDisplayValues))
          && (
            <div style={{ marginBottom: '15px' }}>
              <Heading className={styles.subtitle} level={4}>
                Adjust solution-wide parameters
              </Heading>
              <Card>
                <div className="row">
                  <div className="col-xs-12 col-sm-offset-2 col-sm-8 col-md-offset-2 col-md-8">
                    {job.packageParameters.map((parameter, index) => {
                      const formKey = `${parameter.id}`
                      if (parameter.display && !isConditionallyDisabledFromParameterDisplay(parameter, currentDisplayValues)) {
                        return this.renderMemorisedParameter({
                          ...parameter,
                          formKey,
                        }, index)
                      }
                      return null
                    })}
                    { job.packageId === 'it-tact' && (
                      <div className={styles.saveButton} style={{ marginBottom: '34px' }}>
                        <Button
                          className={styles.save}
                          disabled={Object.keys(isSolutionParamDirty).length === 0 || job._isSettingSolutionParameters}
                          mode="secondary"
                          onClick={() => dispatch(SavePackageParameters(() => this.mapAnalysesOriginalFieldAndValue(), isSolutionParamDirty, app.user.isExternal))}
                        >
                          {job._isSettingSolutionParameters ? 'SAVING ...' : 'SAVE'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          )}
        {selectedAnalyses.length > 0
          && some(
            selectedAnalyses,
            test => test.parameters && test.parameters.length > 0,
          )
          && selectedAnalyses.some(a => a.parameters?.some(parameter => parameter.display && !isConditionallyDisabledFromParameterDisplay(parameter, currentDisplayValues)))
          && (
            <div>
              <Heading className={styles.subtitle} level={4}>
                Adjust analysis parameters
              </Heading>

              <Card noPadding>
                {selectedAnalyses
                  .sort(sortByFunction(x => x.displayOrder))
                  .map((test, index) => ({ index, test }))
                  .filter(
                    o => o.test.parameters && o.test.parameters.length > 0,
                  )
                  .map(o => this.renderTest(o, form, formKeys))}
              </Card>
            </div>
          )}

        {some(form, x => x.error) && (
          <Messagebar className={styles.errorsInParamsWarning} type="error">
            There are problems with the parameters you have filled in, please review and correct them above.
          </Messagebar>
        )}
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    forms: state.forms,
    job: state.job,
    app: state.app,
  }
}

export default connect(mapStateToProps)(EditParameters)
